import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Map, Marker, FeatureVisibility, MapType } from 'mapkit-react';
import api from '../services/api';

interface Pin {
  _id: string;
  location: {
    latitude: number;
    longitude: number;
  };
  note: string;
  media?: { contentType: string; url: string };
  createdBy: string;
}




const MapScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const [projectName, setProjectName] = useState('')
  const [pins, setPins] = useState<Pin[]>([]);
  const [addingPin, setAddingPin] = useState(false);
  const [newPinCoordinate, setNewPinCoordinate] = useState<[number, number] | null>(null);
  const [note, setNote] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [savingPin, setSavingPin] = useState(false);
  const [center, setCenter] = useState<[number, number]>([40.7128, -74.0060]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Apple MapKit JS refs and state
  const [mapkitLoading, setMapkitLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);

  // Media preview state
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaTitle, setMediaTitle] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Project switching for clients - simplified to navigate back to home screen



  // Load pins from API
  const loadPins = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/pins/${projectId}`);
      setPins(response.data);
    } catch (error) {
      console.error('Failed to load pins:', error);
    }
  }, [projectId]);

  // Load project center
  const loadProjectCenter = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}`);
      const project = response.data;
      setProjectName(project.name)
      const location = project.courseLocation || project.courseCenter || project.location;
      if (location?.latitude && location?.longitude) {
        setCenter([location.latitude, location.longitude]);
      }
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  }, [projectId]);

  // Simplified initialization since mapkit-react handles loading
  const initializeMap = useCallback(async () => {
    try {
      setMapkitLoading(true);
      setMapError(null);

      // Load project center and pins
      await loadProjectCenter();
      await loadPins();

      setMapkitLoading(false);
      console.log('Map initialization complete');
    } catch (error: any) {
      console.error('Failed to initialize MapKit:', error);
      setMapError(error.message || 'Failed to load Apple Maps');
      setMapkitLoading(false);
    }
  }, [loadPins, loadProjectCenter]);

  // Initialize map with library
  useEffect(() => {
    initializeMap();
  }, [initializeMap]);

  // Re-initialize when projectId changes (for project switching)
  useEffect(() => {
    if (projectId) {
      // Reset loading states and clear pins when switching projects
      setMapkitLoading(true);
      setPins([]);
      initializeMap();
    }
  }, [projectId, initializeMap]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/mov', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image or video file.');
        return;
      }
      // Validate file size (15MB limit)
      if (file.size > 15 * 1024 * 1024) {
        alert('File size must be less than 15MB.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const savePin = async () => {
    if (!newPinCoordinate || !projectId) return;

    setSavingPin(true);
    try {
      const formData = new FormData();
      formData.append('projectId', projectId);
      formData.append('latitude', newPinCoordinate[0].toString());
      formData.append('longitude', newPinCoordinate[1].toString());
      formData.append('note', note || '');

      if (selectedFile) {
        formData.append('media', selectedFile);
      }

      await api.post('/pins', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Reload pins to include the new one
      await loadPins();

      // Reset form
      setAddingPin(false);
      setNewPinCoordinate(null);
      setNote('');
      setSelectedFile(null);

    } catch (error) {
      console.error('Failed to save pin:', error);
      alert('Failed to save pin. Please try again.');
    } finally {
      setSavingPin(false);
    }
  };

  const cancelAddingPin = () => {
    setAddingPin(false);
    setNewPinCoordinate(null);
    setNote('');
    setSelectedFile(null);
  };

  // Open media preview for pin
  const openMedia = async (pin: Pin) => {
    try {
      setMediaTitle(pin.note || 'Pin Media');
      setMediaLoading(true);
      setShowMediaModal(true);

      // Construct absolute URL for pin media
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const absUrl = `${baseURL}/pins/${pin._id}/media`;

        console.log(absUrl)

      // Download with auth (like mobile app does)
      const response = await fetch(absUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to download media: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Determine type from blob content type or URL
      const contentType = response.headers.get('content-type') || '';
      if (contentType.startsWith('video/')) {
        setMediaType('video');
      } else if (contentType.startsWith('image/')) {
        setMediaType('image');
      } else {
        // Fallback based on extension
        if (absUrl.match(/\.(mp4|mov|m4v|webm|avi)(\?.*)?$/i)) {
          setMediaType('video');
        } else {
          setMediaType('image');
        }
      }

      setMediaUrl(blobUrl);

    } catch (error) {
      console.error('Media preview failed:', error);
      alert('Failed to load media for preview');
      setShowMediaModal(false);
      setMediaType(null);
    } finally {
      setMediaLoading(false);
    }
  };

  const closeMediaModal = () => {
    // Clean up blob URL on close
    if (mediaUrl && mediaUrl.startsWith('blob:')) {
      URL.revokeObjectURL(mediaUrl);
    }
    setShowMediaModal(false);
    setMediaUrl('');
    setMediaType(null);
  };



  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-start mb-6">
          <h1 className="text-2xl font-bold">{projectName} Map</h1>

          {/* Client Navigation */}
          {user?.role === 'client' ? (
            <div className="flex space-x-3">
              {user.projects && user.projects.length > 1 && (
                <button
                  onClick={() => navigate('/')}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Switch Project
                </button>
              )}
              {projectId && (
                <button
                  onClick={() => navigate(`/project/${projectId}`)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Project Details
                </button>
              )}
            </div>
          ) : (
            /* Designer/Admin Navigation */
            <button
              onClick={() => navigate(`/project/${projectId}`)}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Project Details
            </button>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <h3 className="font-semibold text-blue-800 mb-2">How to add pins:</h3>
          <p className="text-blue-700 text-sm">
            Click anywhere on the map to place a pin. You can add notes and upload photos or videos to each pin.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {mapkitLoading ? (
            <div className="flex flex-col justify-center items-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
              <div className="text-lg text-gray-600">Loading Apple Maps...</div>
              {mapError && (
                <div className="mt-4 text-sm text-red-600 text-center max-w-md">
                  Error: {mapError}
                  <br />
                  <br />
                  Please check:
                  <br />• Network connection
                  <br />• Apple Maps token configuration
                  <br />• Domain authorization in Apple Developer Console
                </div>
              )}
            </div>
          ) : mapError ? (
            <div className="flex flex-col justify-center items-center h-96">
              <div className="text-xl text-red-600 mb-4">Failed to Load Apple Maps</div>
              <div className="text-sm text-gray-600 text-center max-w-md mb-4">
                {mapError}
              </div>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <div
              style={{ height: '600px', width: '100%' }}
              className="rounded-lg"
            >
              <Map
                key={projectId || 'default'} // Force remount when project changes
                token={process.env.REACT_APP_APPLE_MAPKITJS || ''}
                onSingleTap={(event) => {
                  if (addingPin) return;
                  const coord = event.toCoordinates();
                  setNewPinCoordinate([coord.latitude, coord.longitude]);
                  setAddingPin(true);
                  setNote('');
                  setSelectedFile(null);
                }}
                initialRegion={{
                  centerLatitude: center[0],
                  centerLongitude: center[1],
                  latitudeDelta: 0.01,
                  longitudeDelta: 0.01
                }}
                showsZoomControl={true}
                showsScale={FeatureVisibility.Visible}
                isRotationEnabled={true}
                mapType={MapType.Satellite}
              >
                {pins.map((pin) => (
                  <Marker
                    key={pin._id}
                    latitude={pin.location.latitude}
                    longitude={pin.location.longitude}
                    title={pin.note || 'Pin'}
                    subtitle={`Lat: ${pin.location.latitude.toFixed(4)}, Lng: ${pin.location.longitude.toFixed(4)}`}
                    color={!!pin.media?.url ? '#DC2626' : '#2563EB'}
                    glyphText={!!pin.media?.url ? '📎' : '📍'}
                    onSelect={() => {
                      if (!!pin.media?.url) {
                        openMedia(pin);
                      }
                    }}
                  />
                ))}
              </Map>
            </div>
          )}

          {projectId && (
            <p className="mt-4 text-gray-600">
              Project ID: {projectId}
            </p>
          )}
        </div>

        {/* Add Pin Modal */}
        {addingPin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-semibold mb-4">Add Pin</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Enter a note for this pin..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Media (optional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="w-full"
                />
                {selectedFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={cancelAddingPin}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={savingPin}
                >
                  Cancel
                </button>
                <button
                  onClick={savePin}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={savingPin}
                >
                  {savingPin ? 'Saving...' : 'Save Pin'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>



      {/* Media Preview Modal */}
      {showMediaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{mediaTitle}</h3>
              <button
                onClick={closeMediaModal}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>

            {mediaLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-lg">Loading media...</div>
              </div>
            ) : (
              <div className="flex justify-center">
                {mediaType === 'image' ? (
                  <img
                    src={mediaUrl}
                    alt={mediaTitle}
                    className="max-w-full max-h-96 object-contain"
                    onError={() => {
                      alert('Failed to load image');
                      closeMediaModal();
                    }}
                  />
                ) : mediaType === 'video' ? (
                  <video
                    src={mediaUrl}
                    controls
                    className="max-w-full max-h-96"
                    onError={() => {
                      alert('Failed to load video');
                      closeMediaModal();
                    }}
                  >
                    Your browser does not support video playback.
                  </video>
                ) : (
                  <div className="text-red-600">Unable to preview this media type</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapScreen;
